import { View, ViewOpts } from '@play-co/timestep-core/ui';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { playSpineAnimation } from 'src/lib/ui/utils/spine';
import { waitForItPromise } from 'src/lib/utils';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import sounds from 'src/lib/sounds';
import { RadialShine } from 'src/game/components/shared/RadialShine';

const assets = {
  cakeLoop: {
    loadMethod: spineLoader,
    url: 'assets/ui/buddies/popupBaking/cake/cake_loop',
  },
  cakeMix: {
    loadMethod: spineLoader,
    url: 'assets/ui/buddies/popupBaking/cake/cake_mix',
  },
  cakeEvolve: 'assets/sounds/sfx/buddies/cake_evolve.mp3',
};

export class BuddiesCake extends View {
  public static getAssets = () => Object.values(assets);

  private shine: RadialShine;
  private cake?: SpineView;
  private cakeMix?: SpineView;
  private currentStage?: number;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
      width: 350,
      height: 350,
      centerOnOrigin: true,
    });

    this.shine = new RadialShine({
      superview: this,
      width: 500,
      height: 500,
      x: this.style.width / 2,
      y: this.style.height / 2 - 60,
      visible: false,
      centerOnOrigin: true,
    });
  }

  private async createCakeLoop() {
    if (this.cake) return;
    // Ensure spine is loaded
    await spineLoader(assets.cakeLoop.url);
    this.cake = new SpineView({
      superview: this,
      spinePath: assets.cakeLoop.url,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
      scale: DOWNSCALE_FACTOR,
    });
  }

  private async createCakeMix() {
    if (this.cakeMix) return;
    // Ensure spine is loaded
    await spineLoader(assets.cakeMix.url);
    this.cakeMix = new SpineView({
      superview: this,
      spinePath: assets.cakeMix.url,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
      scale: DOWNSCALE_FACTOR,
      visible: false,
    });
  }

  public async setStage(args: { stage: number; numStages: number }) {
    if (this.currentStage === args.stage) return;
    await this.createCakeLoop();

    const updateCake = () => {
      if (!this.cake) return;
      const index = args.stage - 1;
      this.cake.state.clearTracks();
      this.cake.state.clearListeners();
      this.cake.skeleton.setToSetupPose();
      void playSpineAnimation(this.cake, {
        animation: `level${index}_idle`,
        loop: true,
        clear: true,
      });
    };

    // Animate the cake transition if we're changing stages.
    if (this.currentStage) {
      await this.animateTransition(updateCake);
    }
    // For the initial stage, just update the cake.
    else {
      updateCake();
    }

    this.currentStage = args.stage;

    if (args.stage === args.numStages) {
      this.shine.show();
    } else {
      this.shine.hide();
    }

    // Cake is only clickable while is not fully built
    this.updateOpts({ canHandleEvents: args.stage < args.numStages });
  }

  private async animateTransition(onCakeHidden?: () => void) {
    sounds.sfx.play('cakeEvolve');
    await this.createCakeMix();

    if (!this.cakeMix) {
      onCakeHidden?.();
      return;
    }
    waitForItPromise(700).then(() => onCakeHidden?.());
    this.cakeMix.show();
    await playSpineAnimation(this.cakeMix, {
      animation: 'baking',
      loop: false,
      clear: true,
    });
    this.cakeMix.hide();
  }
}
